import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X, ChevronDown, Heart, LogOut, Package, Instagram, Mail, Clock, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import CartDrawer from './CartDrawer';
import LoginModal from './LoginModal';
import SearchBar from './SearchBar';
import NotificationSettingsModal from './NotificationSettingsModal';
import { api, apiClient } from '../services/api';
import { setCategorySlugs } from '../utils/urlHelpers';
import { checkIsWorkingHours, getWorkingHoursMessage } from '../utils/workingHours';
import { subscribeToPush } from '../utils/pushNotification';


const getImgSrc = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  if (src.startsWith('data:')) return src;
  if (src.startsWith('/')) return src; // Frontend already serves from public/ or absolute

  // Default to server uploads for other relative paths from DB
  return `https://api.carmengold.com/uploads/${src}`;
};

// Flatten nested categories structure - memoized utility
const flattenCategories = (cats) => {
  const result = [];
  if (!Array.isArray(cats)) return result;
  
  cats.forEach(cat => {
    if (!cat) return;
    result.push(cat);
    const children = cat.children || [];
    children.forEach(child => {
      if (!child) return;
      result.push(child);
      const grandChildren = child.children || [];
      result.push(...grandChildren.filter(Boolean));
    });
  });
  return result;
};

// Subcategory Accordion Component for nested categories
const TakiSubcategoryAccordion = ({ category, children, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!category || !children || children.length === 0) return null;
  
  return (
    <div className="mb-1">
      <div className="flex items-center gap-0">
        {/* Clickable category name - navigates to category page */}
        <Link
          to={`/taki-ve-mucevherat/${category.slug}`}
          onClick={onNavigate}
          className="flex-1 flex items-center gap-3 py-3 text-[15px] text-[#4A3A1A] hover:text-[#D4AF37] transition-all group"
        >
          <div className="w-8 h-8 rounded-lg bg-[#FAF7F0] flex items-center justify-center p-1 group-hover:bg-white transition-colors">
            {category.image ? (
              <img src={getImgSrc(category.image)} alt={category.name} className="w-full h-full object-contain" />
            ) : (
              <div className="w-1 h-1 rounded-full bg-[#D4AF37]/40" />
            )}
          </div>
          <span className="flex-1 text-left font-medium">{category.name}</span>
        </Link>
        {/* Dropdown toggle button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="px-3 py-3 text-[#4A3A1A] hover:text-[#D4AF37] transition-colors"
          aria-label={`${category.name} alt kategorilerini ${isOpen ? 'kapat' : 'aç'}`}
        >
          <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <ul className="pl-12 space-y-1 mt-1">
          {children.map(child => {
            if (!child || !child.id || !child.slug) return null;
            return (
              <li key={child.id}>
                <Link 
                  to={`/taki-ve-mucevherat/${category.slug}/${child.slug}`}
                  onClick={onNavigate}
                  className="flex items-center gap-2 py-2 text-[14px] text-[#6A5A3A] hover:text-[#D4AF37] transition-colors"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/50" />
                  <span>{child.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [mobileSarrafiyeOpen, setMobileSarrafiyeOpen] = useState(false);
  const [mobileTakiOpen, setMobileTakiOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeHoverCat, setActiveHoverCat] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sarrafiyeCats, setSarrafiyeCats] = useState([]);
  const [takiCats, setTakiCats] = useState([]);
  const [priceCountdown, setPriceCountdown] = useState(60);
  const [isMarketOpen, setIsMarketOpen] = useState(null); // null = henüz bilinmiyor
  const [lastPriceTime, setLastPriceTime] = useState(null);
  const { cartCount, setIsCartOpen } = useCart();
  const { user, isLoginOpen, setIsLoginOpen, logout } = useAuth();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const closeCatTimer = useRef(null);
  const [isWorkingHours, setIsWorkingHours] = useState(true);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  // Çalışma saatlerini kontrol et
  useEffect(() => {
    setIsWorkingHours(checkIsWorkingHours());
    
    // Her dakikada bir kontrol et
    const interval = setInterval(() => {
      setIsWorkingHours(checkIsWorkingHours());
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // PWA standalone mode kontrolü
  useEffect(() => {
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone === true;
      setIsPWA(isStandalone);
    };
    
    checkPWA();
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  // Sihirli Bağlantı (Magic Link) Yakalama - PWA Eşleştirme Sistemi
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const searchParams = new URLSearchParams(window.location.search);
        const encodedEmail = searchParams.get('guestEmail');
        const orderId = searchParams.get('orderId');
        
        if (encodedEmail) {
          // Base64'ten çöz
          const decodedEmail = atob(encodedEmail);
          
          // ✅ Email'i PWA local storage'a kaydet (Safari kum havuzu dışına çıkış)
          localStorage.setItem('guestEmail', decodedEmail);
          
          if ('Notification' in window && Notification.permission === 'granted') {
            subscribeToPush({ role: 'user', email: decodedEmail })
              .then(() => {
                localStorage.setItem('syncedGuestEmail', decodedEmail);
              })
              .catch(() => {});
          }
          
          if (orderId) {
            window.location.href = `/siparis-takibi?orderId=${orderId}&fromEmail=1`;
          } else {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          }
        }
      }
    } catch (e) {
      console.error('❌ Magic link parse error:', e);
    }
  }, []);

  // Misafir kullanıcının e-postasını arka planda eşitleme (iOS PWA sorunu için kesin çözüm)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      const guestEmail = localStorage.getItem('guestEmail');
      const syncedEmail = localStorage.getItem('syncedGuestEmail');
      
      if (guestEmail && guestEmail !== syncedEmail) {
        subscribeToPush({ role: 'user', email: guestEmail })
          .then(() => {
            localStorage.setItem('syncedGuestEmail', guestEmail);
          })
          .catch(() => {});
      }
    }
  }, []);

  // Piyasa durumunu kontrol et (her 60 saniyede bir)
  useEffect(() => {
    const checkMarketStatus = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3333/api';
        const res = await fetch(`${apiUrl}/prices/market-status`);
        if (res.ok) {
          const data = await res.json();
          // Backend'den direkt isMarketOpen alanını kullan
          if (typeof data.isMarketOpen === 'boolean') {
            setIsMarketOpen(data.isMarketOpen);
            setLastPriceTime(data.lastMessageTime || null);
          } else {
            // Eski API uyumluluğu: lastPrices'dan hesapla
            const prices = data.lastPrices || {};
            let latestTime = null;
            Object.values(prices).forEach((p) => {
              if (p.time) {
                const t = new Date(p.time);
                if (!latestTime || t > latestTime) latestTime = t;
              }
            });
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
            setIsMarketOpen(latestTime ? latestTime > fiveMinutesAgo : false);
            setLastPriceTime(latestTime ? latestTime.toISOString() : null);
          }
        }
      } catch {
        // sessizce devam et
      }
    };
    checkMarketStatus();
    const id = setInterval(checkMarketStatus, 60000);
    return () => clearInterval(id);
  }, []);

  // Fiyat güncelleme geri sayımı - global zamana göre (her dakikanın 0. saniyesinde yenilenir)
  useEffect(() => {
    let intervalId;

    const updateCountdown = () => {
      const now = new Date();
      const seconds = now.getSeconds();
      const remaining = 60 - seconds;
      setPriceCountdown(remaining === 60 ? 0 : remaining);
    };

    // İlk güncelleme
    updateCountdown();

    // Her 1000ms'de bir güncelle (performans için optimize edildi)
    intervalId = setInterval(updateCountdown, 1000);

    // Fiyat güncellendiğinde sayacı senkronize et
    const onPriceUpdate = () => {
      updateCountdown();
    };
    window.addEventListener('prices:updated', onPriceUpdate);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('prices:updated', onPriceUpdate);
    };
  }, []);
  // Fetch categories from API
  useEffect(() => {
    const CACHE_KEY = 'cg_header_cats';
    const CACHE_TTL = 5 * 60 * 1000; // 5 dakika

    const applyData = (data) => {
      const flattenCategories = (cats) => {
        const result = [];
        cats.forEach(cat => {
          result.push(cat);
          if (cat.children && cat.children.length > 0) {
            cat.children.forEach(child => {
              result.push(child);
              if (child.children && child.children.length > 0) {
                result.push(...child.children);
              }
            });
          }
        });
        return result;
      };
      const sarrafiyeNested = data.filter(c => c.menuSlug === 'sarrafiye');
      const sarrafiye = flattenCategories(sarrafiyeNested).sort((a, b) => a.orderIndex - b.orderIndex);
      const takiNested = data.filter(c => c.menuSlug === 'taki-ve-mucevherat');
      const taki = flattenCategories(takiNested).sort((a, b) => a.orderIndex - b.orderIndex);
      setSarrafiyeCats(sarrafiye);
      setTakiCats(taki);
      setCategorySlugs(sarrafiye.filter(c => !c.parentId), taki.filter(c => !c.parentId));
    };

    const load = async (bypassCache = false) => {
      // Cache kontrolü
      if (!bypassCache) {
        try {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            const { data: cachedData, ts } = JSON.parse(cached);
            if (Date.now() - ts < CACHE_TTL && Array.isArray(cachedData) && cachedData.length > 0) {
              applyData(cachedData);
              return;
            }
          }
        } catch (_) {}
      }

      try {
        const res = await api.getCategories();
        const data = Array.isArray(res) ? res : (res?.data || []);
        applyData(data);
        // Cache'e kaydet
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
        } catch (_) {}
      } catch (err) {
        // Silently handle error in production
      }
    };

    load();

    // Admin panelden kategori güncellemesi gelirse cache'i temizle ve yenile
    const handleCategoryUpdate = () => {
      try { localStorage.removeItem(CACHE_KEY); } catch (_) {}
      load(true);
    };
    window.addEventListener('categoriesUpdated', handleCategoryUpdate);
    return () => window.removeEventListener('categoriesUpdated', handleCategoryUpdate);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/arama?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll effect - only background change, no nav hiding
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mobile menu scroll management is now handled globally in App.js

  return (
    <>
      {/* Mesai dışı havale uyarısı - tadilat barının altında */}
      {!checkIsWorkingHours() && (
        <div className="w-full bg-[#D4AF37] text-[#1a1a1a] text-center text-[13px] sm:text-[14px] font-semibold tracking-wide py-2 px-4 z-[1000] relative flex items-center justify-center gap-2">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>{getWorkingHoursMessage()}</span>
        </div>
      )}

      <header
        className={`sticky top-0 z-[999] transition duration-300 ease-in-out ${isScrolled
          ? 'bg-[#FAF7F0]/95 backdrop-blur-lg shadow-md'
          : 'bg-transparent'
          }`}
        style={{
          /* iOS scroll sırasında görüntü titremesini önler */
          willChange: 'transform',
          /* iOS 26 dahil tüm iPhone modellerinde Notch/Dynamic Island koruması */
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >

        {/* === FİYAT GÜNCELLEME SAYACI - Header içinde sağ üstte sabit === */}
        {!isMobileMenuOpen && (
          <div 
            className="absolute top-2 right-3 z-[1002] flex items-center gap-1.5 select-none pointer-events-none"
            style={{ paddingRight: 'env(safe-area-inset-right, 0px)' }}
          >
            {isMarketOpen === null ? null : isMarketOpen ? (
              <>
                <span className="relative flex h-1.5 w-1.5">
                  {priceCountdown <= 5 && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  )}
                  <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${priceCountdown <= 5 ? 'bg-red-500' : 'bg-[#D4AF37]'}`} />
                </span>
                <span className="text-[12px] font-medium text-[#1a1a1a] tracking-wide whitespace-nowrap">
                  Fiyat Güncellemesi: {String(priceCountdown).padStart(2, '0')} sn
                </span>
              </>
            ) : (
              <>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-gray-400" />
                </span>
                <span className="text-[12px] font-medium text-[#888] tracking-wide whitespace-nowrap">
                  Piyasa Kapalı
                  {lastPriceTime && (
                    <span className="ml-1 text-[11px] text-[#aaa]">
                      · Son: {new Date(lastPriceTime).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </span>
              </>
            )}
          </div>
        )}

        {/* === DESKTOP HEADER ROW (lg+) === */}
        <div className="hidden lg:flex items-center justify-between gap-8 py-4 container mx-auto px-4">
          {/* Left: Search */}
          <div className="flex flex-1 max-w-xs">
            <SearchBar />
          </div>
          {/* Center: Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex-shrink-0 group">
            <img src="/carmen-gold-logo.png" alt="Carmen Gold" loading="eager" decoding="async"
              width="360" height="90"
              className="h-[90px] w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
          </Link>
          {/* Right: Actions */}
          <div className="flex items-center gap-4 flex-1 justify-end max-w-xs">
            {/* Sipariş Takibi */}
            {user ? (
              <button onClick={() => { window.location.href = '/hesabim'; }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/50 transition-all group relative" title="Hesabım">
                <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300" />
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#1a1a1a] group-hover:text-[#D4AF37] transition-colors"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22v-10" /></svg>
              </button>
            ) : (
              <Link to="/siparis-takibi"
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/50 transition-all group relative" title="Sipariş Takibi">
                <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300" />
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#1a1a1a] group-hover:text-[#D4AF37] transition-colors"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22v-10" /></svg>
              </Link>
            )}
            {/* User */}
            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-white/50 rounded-full group transition-all">
                  {user.profileImage ? (
                    <img src={user.profileImage.startsWith('http') ? user.profileImage : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3333'}${user.profileImage}`}
                      alt="Profil" className="w-9 h-9 rounded-full object-cover border-2 border-[#D4AF37] shadow-md" crossOrigin="anonymous" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B4941F] flex items-center justify-center text-white font-bold text-sm shadow-md">
                      {user.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                  <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <button onClick={() => { window.location.href = '/hesabim'; setIsUserMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <User className="h-4 w-4 text-[#D4AF37]" /><span>Hesabım</span>
                    </button>
                    <button onClick={async () => { await logout(); setIsUserMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                      <LogOut className="h-4 w-4" /><span>Çıkış Yap</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setIsLoginOpen(true)}
                className="flex items-center gap-2 px-3 py-2 hover:bg-white/50 rounded-full group">
                <User className="h-5 w-5 text-[#1a1a1a] group-hover:text-[#D4AF37] transition-colors" />
              </Button>
            )}
            {/* Cart */}
            <Button variant="ghost" size="sm" onClick={() => setIsCartOpen(true)}
              className="relative px-3 py-2 hover:bg-white/50 rounded-full group">
              <ShoppingCart className="h-5 w-5 text-[#1a1a1a] group-hover:text-[#D4AF37] transition-colors" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#D4AF37] text-[#1a1a1a] text-[10px] rounded-full flex items-center justify-center font-bold">{cartCount}</span>
              )}
            </Button>
          </div>
        </div>

        {/* === MOBILE HEADER ROW (< lg) === */}
        <div className="flex lg:hidden items-center py-2 px-2 relative min-h-[90px]">

          {/* SEARCH BAR ABSOLUTE WRAPPER: Matches position of old search button and expands to right edge */}
          <div className="absolute left-[64px] right-[156px] h-full flex items-center z-[60] pointer-events-none">
            <div className="pointer-events-auto w-full flex items-center h-full">
              <SearchBar isMobile={false} onActiveChange={setIsMobileSearchOpen} />
            </div>
          </div>

          {/* LEFT: Hamburger + Placeholder */}
          <div className="flex items-center gap-0 flex-1 z-50">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menü"
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#D4AF37]/10 transition-all"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5 text-[#1a1a1a]" /> : <Menu className="h-5 w-5 text-[#1a1a1a]" />}
            </button>
            {/* Placeholder to keep flex-1 balance where the search button used to be */}
            <div className="w-10 h-10 pointer-events-none"></div>
          </div>

          {/* CENTER: Logo */}
          <Link to="/" onClick={() => { window.scrollTo(0, 0); setIsMobileMenuOpen(false); setIsMobileSearchOpen(false); }}
            className={`absolute left-1/2 -translate-x-1/2 flex-shrink-0 z-40 transition-all duration-300 ${isMobileSearchOpen ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}>
            <img src="/carmen-gold-logo.png" alt="Carmen Gold" loading="eager" decoding="async"
              width="224" height="56"
              className="h-[56px] w-auto object-contain" />
          </Link>

          {/* RIGHT: Sipariş Takibi + User + Cart */}
          <div className="flex items-center gap-0 flex-1 justify-end z-50">
            {/* Sipariş Takibi */}
            {user ? (
              <button onClick={() => { window.location.href = '/hesabim'; }} aria-label="Hesabım"
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#D4AF37]/10 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#1a1a1a]"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22v-10" /></svg>
              </button>
            ) : (
              <Link to="/siparis-takibi" aria-label="Sipariş Takibi"
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#D4AF37]/10 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#1a1a1a]"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22v-10" /></svg>
              </Link>
            )}
            {/* User */}
            {user ? (
              <button
                onClick={() => { window.location.href = '/hesabim'; }}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#D4AF37]/10 transition-all"
                title="Hesabım"
              >
                {user.profileImage ? (
                  <img src={user.profileImage.startsWith('http') ? user.profileImage : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3333'}${user.profileImage}`}
                    alt="Profil" className="w-8 h-8 rounded-full object-cover border-2 border-[#D4AF37]" crossOrigin="anonymous" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B4941F] flex items-center justify-center text-white font-bold text-sm">
                    {user.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
              </button>
            ) : (
              <button onClick={() => setIsLoginOpen(true)} aria-label="Giriş Yap"
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#D4AF37]/10 transition-all">
                <User className="h-5 w-5 text-[#1a1a1a]" />
              </button>
            )}
            {/* Cart */}
            <button onClick={() => setIsCartOpen(true)} aria-label="Sepet"
              className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#D4AF37]/10 transition-all">
              <ShoppingCart className="h-5 w-5 text-[#1a1a1a]" />
              {cartCount > 0 && (
                <span className="absolute top-1 right-0 min-w-[16px] h-[16px] px-0.5 bg-[#D4AF37] text-[#1a1a1a] text-[9px] rounded-full flex items-center justify-center font-bold">{cartCount}</span>
              )}
            </button>
          </div>
        </div>

        {/* Navigation - Desktop (Hides on scroll down) */}
        <nav
          className="relative hidden lg:block border-t border-[#D4AF37]/20"
        >
          <div className="container mx-auto px-4">
            <ul className="flex items-center justify-center gap-1">
              {/* Anasayfa */}
              <li>
                <Link
                  to="/"
                  onClick={() => window.scrollTo(0, 0)}
                  className="flex items-center gap-1.5 px-5 py-4 text-[#1a1a1a] hover:text-[#D4AF37] 
                           font-medium text-sm tracking-wide uppercase transition-colors relative group"
                >
                  Anasayfa
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>

              {/* Sarrafiye — Tiffany-style mega menu */}
              <li
                className="relative"
                onMouseEnter={() => { clearTimeout(closeCatTimer.current); setActiveCategory('sarrafiye'); setActiveHoverCat(null); }}
                onMouseLeave={() => { closeCatTimer.current = setTimeout(() => { setActiveCategory(null); setActiveHoverCat(null); }, 180); }}
              >
                <Link to="/sarrafiye" className="flex items-center gap-1.5 px-5 py-4 text-[#1a1a1a] hover:text-[#D4AF37] font-medium text-sm tracking-wide uppercase transition-colors relative group cursor-pointer">
                  Sarrafiye
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${activeCategory === 'sarrafiye' ? 'rotate-180 text-[#D4AF37]' : ''}`} />
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
                </Link>

                {activeCategory === 'sarrafiye' && sarrafiyeCats.length > 0 && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 z-[200] bg-white border border-[#D4AF37]/30 shadow-[0_12px_40px_rgba(0,0,0,0.14)] rounded-2xl overflow-hidden"
                    style={{ width: '520px', animation: 'tiffanyMenuIn 0.22s ease forwards', marginTop: '4px' }}
                    onMouseEnter={() => clearTimeout(closeCatTimer.current)}
                    onMouseLeave={() => { closeCatTimer.current = setTimeout(() => { setActiveCategory(null); setActiveHoverCat(null); }, 180); }}
                  >
                    {/* Altın üst çizgi */}
                    <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37 20%, #D4AF37 80%, transparent)' }} />
                    <div className="flex" style={{ height: '280px' }}>
                      {/* Sol: Ana kategoriler — 6'dan fazla ise aşağı scroll */}
                      <div className="w-56 border-r border-[#D4AF37]/10 py-5 bg-[#FDFCF8] flex flex-col overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#D4AF37 transparent' }}>
                        {sarrafiyeCats.filter(s => !s.parentId).map(sub => {
                          const isActive = activeHoverCat === sub.id;
                          const hasChildren = sarrafiyeCats.some(c => c.parentId === sub.id);
                          return (
                            <div key={sub.id} onMouseEnter={() => setActiveHoverCat(sub.id)}>
                              <Link
                                to={`/sarrafiye/${sub.slug}`}
                                className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-all duration-150 ${isActive ? 'text-[#D4AF37] font-semibold bg-[#FAF7F0]' : 'text-[#2C2C2C] hover:text-[#D4AF37] hover:bg-[#FAF7F0] font-medium'}`}
                              >
                                {sub.image && (
                                  <img src={getImgSrc(sub.image)} alt={sub.name}
                                    className={`w-8 h-8 object-contain rounded-lg flex-shrink-0 transition-all duration-200 ${isActive ? 'scale-110' : 'opacity-70'}`} />
                                )}
                                <span className="flex-1">{sub.name}</span>
                                {hasChildren && <ChevronDown className={`h-3 w-3 -rotate-90 flex-shrink-0 transition-colors ${isActive ? 'text-[#D4AF37]' : 'text-gray-300'}`} />}
                              </Link>
                            </div>
                          );
                        })}
                        <div className="px-5 pt-4 mt-auto border-t border-[#D4AF37]/10">
                          <Link to="/sarrafiye" className="font-['Cormorant_Garamond'] italic text-[#D4AF37] text-sm tracking-[0.1em] hover:text-[#B8941F] transition-colors flex items-center gap-1 group/all">
                            Tümünü Gör
                            <span className="transition-transform duration-200 group-hover/all:translate-x-0.5">→</span>
                          </Link>
                        </div>
                      </div>

                      {/* Sağ: Alt kategoriler */}
                      <div className="flex-1 py-5 px-6 relative bg-white overflow-hidden">
                        {sarrafiyeCats.filter(s => !s.parentId).map((sub, si) => {
                          const children = sarrafiyeCats.filter(c => c.parentId === sub.id);
                          const isActive = activeHoverCat === sub.id;
                          return (
                            <div
                              key={sub.id}
                              className="absolute inset-0 py-5 px-6 flex flex-col justify-between"
                              style={{
                                opacity: isActive ? 1 : 0,
                                transform: isActive ? 'translateX(0)' : 'translateX(8px)',
                                pointerEvents: isActive ? 'auto' : 'none',
                                transition: 'opacity 0.2s ease, transform 0.25s cubic-bezier(0.16,1,0.3,1)',
                              }}
                            >
                              {/* Başlık */}
                              <p className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-[0.2em] mb-4">{sub.name}</p>
                              {/* Alt kategoriler */}
                              {children.length > 0 ? (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-0 flex-1">
                                  {children.map((child, i) => (
                                    <Link
                                      key={child.id}
                                      to={`/sarrafiye/${sub.slug}/${child.slug}`}
                                      className="py-1.5 text-sm text-[#5A5A5A] hover:text-[#D4AF37] transition-all duration-150 font-medium"
                                      style={{
                                        opacity: isActive ? 1 : 0,
                                        transform: isActive ? 'translateY(0)' : 'translateY(4px)',
                                        transition: `color 0.15s, opacity 0.2s ease ${i * 25}ms, transform 0.2s ease ${i * 25}ms`,
                                      }}
                                    >
                                      {child.name}
                                    </Link>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex-1 flex items-center justify-center opacity-40">
                                  <p className="font-['Cormorant_Garamond'] italic text-[15px] text-[#8A8A8A]">Alt kategori bulunmamaktadır.</p>
                                </div>
                              )}
                              {/* Koleksiyonu Gör — sağ alt */}
                              <div className="flex justify-end pt-3 border-t border-[#D4AF37]/10">
                                <Link
                                  to={`/sarrafiye/${sub.slug}`}
                                  className="font-['Cormorant_Garamond'] italic text-[#D4AF37] text-sm tracking-[0.1em] hover:text-[#B8941F] transition-colors flex items-center gap-1 group/link"
                                >
                                  Koleksiyonu Gör
                                  <span className="transition-transform duration-200 group-hover/link:translate-x-0.5">→</span>
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                        {!activeHoverCat && (
                          <div className="flex items-center h-full opacity-30">
                            <p className="font-['Cormorant_Garamond'] italic text-base text-[#8A8A8A]">Kategori seçin</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </li>

              {/* Takı ve Mücevherat — Tiffany-style mega menu */}
              <li
                className="relative"
                onMouseEnter={() => { clearTimeout(closeCatTimer.current); setActiveCategory('taki'); setActiveHoverCat(null); }}
                onMouseLeave={() => { closeCatTimer.current = setTimeout(() => { setActiveCategory(null); setActiveHoverCat(null); }, 180); }}
              >
                <Link to="/taki-ve-mucevherat" className="flex items-center gap-1.5 px-5 py-4 text-[#1a1a1a] hover:text-[#D4AF37] font-medium text-sm tracking-wide uppercase transition-colors relative group cursor-pointer">
                  Takı ve Mücevherat
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${activeCategory === 'taki' ? 'rotate-180 text-[#D4AF37]' : ''}`} />
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
                </Link>

                {activeCategory === 'taki' && takiCats.length > 0 && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 z-[200] bg-white border border-[#D4AF37]/30 shadow-[0_12px_40px_rgba(0,0,0,0.14)] rounded-2xl overflow-hidden"
                    style={{ width: '520px', animation: 'tiffanyMenuIn 0.22s ease forwards', marginTop: '4px' }}
                    onMouseEnter={() => clearTimeout(closeCatTimer.current)}
                    onMouseLeave={() => { closeCatTimer.current = setTimeout(() => { setActiveCategory(null); setActiveHoverCat(null); }, 180); }}
                  >
                    <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37 20%, #D4AF37 80%, transparent)' }} />
                    <div className="flex" style={{ height: '280px' }}>
                      {/* Sol: Ana kategoriler — 6'dan fazla ise aşağı scroll */}
                      <div className="w-56 border-r border-[#D4AF37]/10 py-5 bg-[#FDFCF8] flex flex-col overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#D4AF37 transparent' }}>
                        {takiCats.filter(s => !s.parentId).map(sub => {
                          const isActive = activeHoverCat === sub.id;
                          const hasChildren = takiCats.some(c => c.parentId === sub.id);
                          return (
                            <div key={sub.id} onMouseEnter={() => setActiveHoverCat(sub.id)}>
                              <Link
                                to={`/taki-ve-mucevherat/${sub.slug}`}
                                className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-all duration-150 ${isActive ? 'text-[#D4AF37] font-semibold bg-[#FAF7F0]' : 'text-[#2C2C2C] hover:text-[#D4AF37] hover:bg-[#FAF7F0] font-medium'}`}
                              >
                                {sub.image && (
                                  <img src={getImgSrc(sub.image)} alt={sub.name}
                                    className={`w-8 h-8 object-contain rounded-lg flex-shrink-0 transition-all duration-200 ${isActive ? 'scale-110' : 'opacity-70'}`} />
                                )}
                                <span className="flex-1">{sub.name}</span>
                                {hasChildren && <ChevronDown className={`h-3 w-3 -rotate-90 flex-shrink-0 transition-colors ${isActive ? 'text-[#D4AF37]' : 'text-gray-300'}`} />}
                              </Link>
                            </div>
                          );
                        })}
                        <div className="px-5 pt-4 mt-auto border-t border-[#D4AF37]/10">
                          <Link to="/taki-ve-mucevherat" className="font-['Cormorant_Garamond'] italic text-[#D4AF37] text-sm tracking-[0.1em] hover:text-[#B8941F] transition-colors flex items-center gap-1 group/all">
                            Tümünü Gör
                            <span className="transition-transform duration-200 group-hover/all:translate-x-0.5">→</span>
                          </Link>
                        </div>
                      </div>

                      {/* Sağ: Alt kategoriler */}
                      <div className="flex-1 py-5 px-6 relative bg-white overflow-hidden">
                        {takiCats.filter(s => !s.parentId).map((sub, si) => {
                          const children = takiCats.filter(c => c.parentId === sub.id);
                          const isActive = activeHoverCat === sub.id;
                          return (
                            <div
                              key={sub.id}
                              className="absolute inset-0 py-5 px-6 flex flex-col justify-between"
                              style={{
                                opacity: isActive ? 1 : 0,
                                transform: isActive ? 'translateX(0)' : 'translateX(8px)',
                                pointerEvents: isActive ? 'auto' : 'none',
                                transition: 'opacity 0.2s ease, transform 0.25s cubic-bezier(0.16,1,0.3,1)',
                              }}
                            >
                              <p className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-[0.2em] mb-4">{sub.name}</p>
                              {children.length > 0 ? (
                                <div className="grid grid-cols-2 gap-x-4 gap-y-0 flex-1">
                                  {children.map((child, i) => (
                                    <Link
                                      key={child.id}
                                      to={`/taki-ve-mucevherat/${sub.slug}/${child.slug}`}
                                      className="py-1.5 text-sm text-[#5A5A5A] hover:text-[#D4AF37] font-medium"
                                      style={{
                                        opacity: isActive ? 1 : 0,
                                        transform: isActive ? 'translateY(0)' : 'translateY(4px)',
                                        transition: `color 0.15s, opacity 0.2s ease ${i * 25}ms, transform 0.2s ease ${i * 25}ms`,
                                      }}
                                    >
                                      {child.name}
                                    </Link>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex-1 flex items-center justify-center opacity-40">
                                  <p className="font-['Cormorant_Garamond'] italic text-[15px] text-[#8A8A8A]">Alt kategori bulunmamaktadır.</p>
                                </div>
                              )}
                              {/* Koleksiyonu Gör — sağ alt */}
                              <div className="flex justify-end pt-3 border-t border-[#D4AF37]/10">
                                <Link
                                  to={`/taki-ve-mucevherat/${sub.slug}`}
                                  className="font-['Cormorant_Garamond'] italic text-[#D4AF37] text-sm tracking-[0.1em] hover:text-[#B8941F] transition-colors flex items-center gap-1 group/link"
                                >
                                  Koleksiyonu Gör
                                  <span className="transition-transform duration-200 group-hover/link:translate-x-0.5">→</span>
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                        {!activeHoverCat && (
                          <div className="flex items-center h-full opacity-30">
                            <p className="font-['Cormorant_Garamond'] italic text-base text-[#8A8A8A]">Kategori seçin</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </li>

              {/* Canlı Piyasalar */}
              <li>
                <Link
                  to="/canli-piyasalar"
                  className="flex items-center gap-1.5 px-5 py-4 text-[#1a1a1a] hover:text-[#D4AF37] 
                           font-medium text-sm tracking-wide uppercase transition-colors relative group"
                >
                  Canlı Piyasalar
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>



              {/* Hakkımızda */}
              <li>
                <Link
                  to="/hakkimizda"
                  className="flex items-center gap-1.5 px-5 py-4 text-[#1a1a1a] hover:text-[#D4AF37] 
                           font-medium text-sm tracking-wide uppercase transition-colors relative group"
                >
                  Hakkımızda
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>

              {/* İletişim */}
              <li>
                <Link
                  to="/iletisim"
                  className="flex items-center gap-1.5 px-5 py-4 text-[#1a1a1a] hover:text-[#D4AF37] 
                           font-medium text-sm tracking-wide uppercase transition-colors relative group"
                >
                  İletişim
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full"></span>
                </Link>
              </li>
            </ul>
          </div>
        </nav>
      </header>

      {isMobileMenuOpen && (
        <div
          onClick={() => setIsMobileMenuOpen(false)}
          className="fixed inset-0 z-[1000] lg:hidden bg-black/50 backdrop-blur-md"
          data-scroll-lock="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            minHeight: '100dvh',
            WebkitBackdropFilter: 'blur(12px)',
            backdropFilter: 'blur(12px)',
          }}
        />
      )}

      {/* === MOBILE SIDEBAR PANEL === */}
      <div
        className={`fixed top-0 left-0 h-[100dvh] w-[82%] max-w-[320px] z-[1001] lg:hidden flex flex-col shadow-2xl
          transform transition-transform duration-500 ease-[cubic-bezier(0.32,0,0.67,0)] ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          } bg-[#FAF7F0]/95 backdrop-blur-xl border-r border-[#D4AF37]/20`}
        style={{
          WebkitBackdropFilter: 'blur(20px)',
          backdropFilter: 'blur(20px)',
        }}
      >
          {/* Dekoratif üst gold çizgi */}
          <div className="h-1 w-full shrink-0" style={{ background: 'linear-gradient(90deg, #B4941F, #D4AF37, #F0D060, #D4AF37, #B4941F)' }} />

          {/* HEADER (Bell + Logo + Close) */}
          <div className="relative flex items-center justify-center px-6 pt-4 pb-4 shrink-0 border-b border-[#D4AF37]/10 bg-white">
            {isPWA && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsNotificationModalOpen(true);
                }}
                aria-label="Bildirimler"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/40 hover:bg-white transition-all duration-300 active:scale-95 border border-[#D4AF37]/20 shadow-sm"
              >
                <Bell className="h-5 w-5 text-[#1a1a1a]" />
                {'Notification' in window && Notification.permission === 'denied' && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white" />
                )}
              </button>
            )}
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="mx-auto">
              <img src="/carmen-gold-logo.png" alt="Carmen Gold" loading="eager" 
                width="280" height="70"
                className="h-[70px] w-auto object-contain" />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Menüyü kapat"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/40 hover:bg-white transition-all duration-300 hover:rotate-90 hover:scale-110 active:scale-95 border border-[#D4AF37]/20 shadow-sm"
            >
              <X className="h-5 w-5 text-[#1a1a1a]" />
            </button>
          </div>

          {/* USER QUICK ACCESS (Top) */}
          <div className="px-6 py-6 bg-white/30 border-b border-[#D4AF37]/10 shrink-0">
            {user ? (
              <div className="flex items-center gap-4">
                <div className="relative">
                  {user.profileImage ? (
                    <img
                      src={user.profileImage.startsWith('http') ? user.profileImage : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3333'}${user.profileImage}`}
                      alt="Profil" className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]" crossOrigin="anonymous" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B4941F] flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {user.firstName ? user.firstName.charAt(0).toUpperCase() : 'K'}
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-[#FAF7F0] rounded-full" />
                </div>
                <div className="min-w-0">
                  <p className="text-base font-semibold text-[#1a1a1a] truncate leading-tight">{user.firstName} {user.lastName}</p>
                  <p className="text-[13px] text-[#8A7A5A] truncate mb-1">{user.email}</p>
                  <button
                    onClick={() => { window.location.href = '/profile'; setIsMobileMenuOpen(false); }}
                    className="text-[12px] font-bold text-[#D4AF37] underline decoration-[#D4AF37]/30 underline-offset-4"
                  >
                    Profilimi Yönet
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setIsLoginOpen(true); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border border-[#D4AF37]/25 shadow-sm hover:border-[#D4AF37] transition-all group"
              >
                <div className="flex items-center gap-4 text-left">
                  <div className="w-12 h-12 rounded-full bg-[#FAF7F0] flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-white transition-all">
                    <User className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#1a1a1a]">Giriş Yap / Kayıt Ol</p>
                    <p className="text-[11px] text-[#8A7A5A]">Ayrıcalıklar dünyasına katılın</p>
                  </div>
                </div>
                <ChevronDown className="h-5 w-5 text-[#D4AF37] -rotate-90" />
              </button>
            )}
          </div>

          {/* SCROLLABLE NAV */}
          <nav className="flex-1 overflow-y-auto px-4 py-4 custom-scrollbar" style={{ scrollbarWidth: 'none' }}>
            <ul className="space-y-1">
              {/* Navigation Group Header */}
              <li className="px-3 mb-2">
                <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] mb-1 opacity-70">Menü</p>
                <div className="h-px w-8 bg-[#D4AF37]/30" />
              </li>

              {/* Anasayfa */}
              <li>
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between px-4 py-3 rounded-2xl text-[#1a1a1a] hover:bg-white transition-all border border-transparent hover:border-[#D4AF37]/10 hover:shadow-sm">
                  <span className="flex items-center">
                    <span className="font-['Cormorant_Garamond'] text-xl font-bold uppercase tracking-wide">Anasayfa</span>
                  </span>
                </Link>
              </li>

              {/* Sarrafiye Accordion */}
              <li>
                <button
                  onClick={() => setMobileSarrafiyeOpen(v => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl text-[16px] font-medium transition-all border ${mobileSarrafiyeOpen ? 'bg-white border-[#D4AF37]/30 shadow-sm text-[#D4AF37]' : 'text-[#1a1a1a] bg-transparent border-transparent'
                    }`}
                >
                  <span className="flex items-center">
                    <span className="font-['Cormorant_Garamond'] text-xl font-bold uppercase tracking-wide">Sarrafiye</span>
                  </span>
                  <ChevronDown className={`h-5 w-5 transition-transform duration-500 ${mobileSarrafiyeOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${mobileSarrafiyeOpen ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                  <ul className="grid grid-cols-1 gap-1 pl-10 pr-2">
                    <li>
                      <Link to="/sarrafiye" onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center justify-between py-2 text-[14px] font-bold text-[#D4AF37] hover:text-[#B4941F] transition-colors border-b border-[#D4AF37]/10">
                        Tüm Sarrafiye Ürünleri
                        <ChevronDown className="h-3 w-3 -rotate-90 opacity-40" />
                      </Link>
                    </li>
                    {sarrafiyeCats.filter(c => !c.parentId).map(sub => (
                      <li key={sub.id}>
                        <Link to={`/sarrafiye/${sub.slug}`} onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 py-3 text-[15px] text-[#4A3A1A] hover:text-[#D4AF37] transition-all group">
                          <div className="w-8 h-8 rounded-lg bg-[#FAF7F0] flex items-center justify-center p-1 group-hover:bg-white transition-colors">
                            {sub.image ? (
                              <img src={getImgSrc(sub.image)} alt={sub.name} className="w-full h-full object-contain" />
                            ) : (
                              <div className="w-1 h-1 rounded-full bg-[#D4AF37]/40" />
                            )}
                          </div>
                          <span className="flex-1">{sub.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>

              {/* Mücevherat Accordion */}
              <li>
                <button
                  onClick={() => setMobileTakiOpen(v => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl text-[16px] font-medium transition-all border ${mobileTakiOpen ? 'bg-white border-[#D4AF37]/30 shadow-sm text-[#D4AF37]' : 'text-[#1a1a1a] bg-transparent border-transparent'
                    }`}
                >
                  <span className="flex items-center flex-1 pr-4">
                    <span className="font-['Cormorant_Garamond'] text-xl font-bold uppercase tracking-wide text-left leading-tight">Takı ve Mücevherat</span>
                  </span>
                  <ChevronDown className={`h-5 w-5 shrink-0 transition-transform duration-500 ${mobileTakiOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${mobileTakiOpen ? 'max-h-[1000px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                  <ul className="grid grid-cols-1 gap-1 pl-10 pr-2">
                    <li>
                      <Link to="/taki-ve-mucevherat" onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center justify-between py-2 text-[14px] font-bold text-[#D4AF37] hover:text-[#B4941F] transition-colors border-b border-[#D4AF37]/10">
                        Tüm Takı ve Mücevherat Ürünleri
                        <ChevronDown className="h-3 w-3 -rotate-90 opacity-40" />
                      </Link>
                    </li>
                    {takiCats.map(sub => {
                      if (!sub || !sub.id) return null;
                      
                      // Check if this category has children (subcategories)
                      const hasChildren = takiCats.some(cat => cat.parentId === sub.id);
                      const children = takiCats.filter(cat => cat.parentId === sub.id);
                      
                      // Only show parent categories (those without parentId)
                      if (sub.parentId) return null;
                      
                      return (
                        <li key={sub.id}>
                          {hasChildren ? (
                            <TakiSubcategoryAccordion 
                              category={sub} 
                              children={children}
                              onNavigate={() => setIsMobileMenuOpen(false)}
                            />
                          ) : (
                            <Link to={`/taki-ve-mucevherat/${sub.slug}`} onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center gap-3 py-3 text-[15px] text-[#4A3A1A] hover:text-[#D4AF37] transition-all group">
                              <div className="w-8 h-8 rounded-lg bg-[#FAF7F0] flex items-center justify-center p-1 group-hover:bg-white transition-colors">
                                {sub.image ? (
                                  <img src={getImgSrc(sub.image)} alt={sub.name} className="w-full h-full object-contain" />
                                ) : (
                                  <div className="w-1 h-1 rounded-full bg-[#D4AF37]/40" />
                                )}
                              </div>
                              <span className="flex-1">{sub.name}</span>
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </li>

              {/* Canlı Piyasalar */}
              <li>
                <Link to="/canli-piyasalar" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between px-4 py-3 rounded-2xl text-[#1a1a1a] hover:bg-white transition-all border border-transparent hover:border-[#D4AF37]/10 hover:shadow-sm">
                  <span className="flex items-center">
                    <span className="font-['Cormorant_Garamond'] text-xl font-bold uppercase tracking-wide">Canlı Piyasalar</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] bg-red-500 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter">Live</span>
                  </div>
                </Link>
              </li>

              {/* Hakkımızda */}
              <li>
                <Link to="/hakkimizda" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between px-4 py-3 rounded-2xl text-[#1a1a1a] hover:bg-white transition-all border border-transparent hover:border-[#D4AF37]/10 hover:shadow-sm">
                  <span className="flex items-center">
                    <span className="font-['Cormorant_Garamond'] text-xl font-bold uppercase tracking-wide">Hakkımızda</span>
                  </span>
                </Link>
              </li>

              {/* İletişim */}
              <li>
                <Link to="/iletisim" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between px-4 py-3 rounded-2xl text-[#1a1a1a] hover:bg-white transition-all border border-transparent hover:border-[#D4AF37]/10 hover:shadow-sm">
                  <span className="flex items-center">
                    <span className="font-['Cormorant_Garamond'] text-xl font-bold uppercase tracking-wide">İletişim</span>
                  </span>
                </Link>
              </li>

              {/* Utility Section */}
              <li className="pt-4 pb-1 px-3">
                <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-[0.2em] mb-1 opacity-70">
                  {user ? 'Hesabım' : 'Misafir'}
                </p>
                <div className="h-px w-8 bg-[#D4AF37]/30" />
              </li>

              <li>
                {user ? (
                  <button onClick={() => { window.location.href = '/hesabim'; setIsMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] font-bold text-[#D4AF37] hover:bg-white/50 transition-all">
                    <Package className="h-4 w-4 text-[#D4AF37]" />
                    Sipariş Takibi
                  </button>
                ) : (
                  <Link to="/siparis-takibi" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-4 px-4 py-3 rounded-xl text-[15px] font-bold text-[#D4AF37] hover:bg-white/50 transition-all">
                    <Package className="h-4 w-4 text-[#D4AF37]" />
                    Sipariş Takibi
                  </Link>
                )}
              </li>


            </ul>
          </nav>

          {/* SIDEBAR FOOTER */}
          {user && (
            <div className="shrink-0 px-6 py-3 border-t border-[#D4AF37]/10 bg-white/20">
              <button
                onClick={async () => { await logout(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-[13px] text-red-600 bg-red-50/50 border border-red-100/50 hover:bg-red-50 transition-all"
              >
                <LogOut className="h-4 w-4" /> Çıkış Yap
              </button>
            </div>
          )}
        </div>

      <CartDrawer />
      <LoginModal />
      <NotificationSettingsModal 
        isOpen={isNotificationModalOpen} 
        onClose={() => setIsNotificationModalOpen(false)}
        userRole={user ? 'user' : 'user'}
        userId={user?.id || null}
        email={user?.email || localStorage.getItem('guestEmail') || localStorage.getItem('syncedGuestEmail')}
      />
    </>
  );
};

export default Header;

